import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, Clock, MapPin } from 'lucide-react';
import { atlasApi, AtlasUser, Treinamento } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  onAbrir: (id: string) => void;
}

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** 'YYYY-MM-DD' (ou ISO) -> chave de dia local, sem passar por Date (evita shift de fuso). */
function chaveDia(iso?: string | null): string | null {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function chaveDe(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Todos os dias entre início e fim (inclusive). Sem fim = só o dia de início. */
function diasDoTreinamento(t: Treinamento): string[] {
  const ini = chaveDia(t.data_inicio);
  if (!ini) return [];
  const fim = chaveDia(t.data_fim);
  if (!fim || fim <= ini) return [ini];
  const out: string[] = [];
  const cur = new Date(`${ini}T12:00:00`);
  const alvo = new Date(`${fim}T12:00:00`);
  // Guarda contra intervalos absurdos digitados por engano
  while (cur <= alvo && out.length < 366) {
    out.push(chaveDe(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const CORES: Record<string, string> = {
  publicado: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  rascunho: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
  encerrado: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
};

const Calendario: React.FC<Props> = ({ user, onAbrir }) => {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth()); // 0-11
  const [itens, setItens] = useState<Treinamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [diaSel, setDiaSel] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    // O catálogo de treinamentos é pequeno; carrega tudo e agenda no cliente.
    atlasApi.listarTreinamentos(user, { per_page: 200 })
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => carregar(), [carregar]);

  // dia (YYYY-MM-DD) -> treinamentos que ocorrem nele
  const porDia = useMemo(() => {
    const mapa = new Map<string, Treinamento[]>();
    itens.forEach(t => {
      diasDoTreinamento(t).forEach(d => {
        const lista = mapa.get(d);
        if (lista) lista.push(t);
        else mapa.set(d, [t]);
      });
    });
    return mapa;
  }, [itens]);

  // Grade: começa no domingo da semana do dia 1
  const celulas = useMemo(() => {
    const primeiro = new Date(ano, mes, 1);
    const inicio = new Date(primeiro);
    inicio.setDate(1 - primeiro.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(inicio);
      d.setDate(inicio.getDate() + i);
      return d;
    });
  }, [ano, mes]);

  const navegar = (delta: number) => {
    setDiaSel(null);
    const d = new Date(ano, mes + delta, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth());
  };

  const irParaHoje = () => {
    setDiaSel(null);
    setAno(hoje.getFullYear());
    setMes(hoje.getMonth());
  };

  const hojeKey = chaveDe(hoje);
  const semData = itens.filter(t => !chaveDia(t.data_inicio));
  const doDiaSel = diaSel ? (porDia.get(diaSel) || []) : [];

  return (
    <div className="space-y-3">
      {/* Cabeçalho do mês */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => navegar(-1)} className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" title="Mês anterior">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="font-bold text-gray-900 min-w-[10rem] text-center">{MESES[mes]} {ano}</div>
        <button onClick={() => navegar(1)} className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" title="Próximo mês">
          <ChevronRight className="w-4 h-4" />
        </button>
        <button onClick={irParaHoje} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50">
          Hoje
        </button>
        <span className="ml-auto text-xs text-gray-400">
          {loading ? 'Carregando...' : `${itens.length} treinamento${itens.length === 1 ? '' : 's'} no catálogo`}
        </span>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando calendário...</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
            {DIAS.map(d => <div key={d} className="px-2 py-2 text-center font-semibold">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {celulas.map(d => {
              const key = chaveDe(d);
              const doMes = d.getMonth() === mes;
              const eventos = porDia.get(key) || [];
              const ehHoje = key === hojeKey;
              return (
                <button
                  key={key}
                  onClick={() => setDiaSel(eventos.length ? (diaSel === key ? null : key) : null)}
                  className={`text-left min-h-[92px] border-t border-r border-gray-100 p-1.5 align-top transition ${
                    doMes ? 'bg-white' : 'bg-gray-50/60'
                  } ${diaSel === key ? 'ring-2 ring-inset ring-purple-300' : ''} ${eventos.length ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className={`text-xs font-semibold mb-1 inline-flex items-center justify-center w-6 h-6 rounded-full ${
                    ehHoje ? 'bg-purple-600 text-white' : doMes ? 'text-gray-700' : 'text-gray-300'
                  }`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {eventos.slice(0, 2).map(t => (
                      <div
                        key={t._id}
                        onClick={ev => { ev.stopPropagation(); onAbrir(t._id); }}
                        title={t.titulo}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate ${CORES[t.status] || CORES.rascunho}`}
                      >
                        {t.obrigatorio ? '★ ' : ''}{t.titulo}
                      </div>
                    ))}
                    {eventos.length > 2 && (
                      <div className="text-[10px] text-gray-400 px-1">+{eventos.length - 2} outro(s)</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Detalhe do dia clicado */}
      {diaSel && doDiaSel.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-purple-600" />
            {new Date(`${diaSel}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
          </h3>
          <ul className="space-y-2">
            {doDiaSel.map(t => (
              <li key={t._id}>
                <button onClick={() => onAbrir(t._id)} className="w-full text-left px-3 py-2 rounded-xl border border-gray-100 hover:border-purple-300 hover:bg-purple-50/40 transition">
                  <div className="font-medium text-gray-900">{t.titulo}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 mt-0.5">
                    {t.carga_horaria ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.carga_horaria}h</span> : null}
                    {t.local && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.local}</span>}
                    {t.instrutor && <span>{t.instrutor}</span>}
                    {t.obrigatorio && <span className="text-amber-600 font-medium">Obrigatório</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sem data definida — não aparecem na grade */}
      {!loading && semData.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-2">Sem data definida</h3>
          <div className="flex flex-wrap gap-2">
            {semData.map(t => (
              <button key={t._id} onClick={() => onAbrir(t._id)} className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700 transition">
                {t.titulo}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendario;
